/** @module @liquidefy/code-editor/terminal/terminal-buffer @version 1.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  TerminalBufferState,
  TerminalBufferId,
  TerminalCell,
  TerminalLine,
  TerminalColor,
  TerminalCursorState,
  ScrollbackConfig,
  CellFlags,
  Range,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asTerminalBufferId,
  clamp,
  defaultTerminalColor,
  createCell,
  DEFAULT_SCROLLBACK_CONFIG,
} from '../../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 BUFFER CREATION — createBuffer, createEmptyLine, createCell, defaults
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * The default empty cell: a space with default colors and no flags.
 */
export const EMPTY_CELL: TerminalCell = {
  char: ' ',
  width: 1,
  fg: { mode: 'default' },
  bg: { mode: 'default' },
  flags: 0,
};

/**
 * Create a new terminal buffer state.
 */
export function createBuffer(
  id: string,
  cols: number,
  rows: number,
  scrollbackConfig?: Partial<ScrollbackConfig>
): EditorResult<TerminalBufferState> {
  if (cols < 1 || rows < 1) {
    return err('BUFFER_INVALID_SIZE', `Buffer size must be positive, got ${cols}x${rows}`);
  }

  const lines: TerminalLine[] = [];
  for (let i = 0; i < rows; i++) {
    lines.push(createEmptyLine(cols));
  }

  const config: ScrollbackConfig = {
    ...DEFAULT_SCROLLBACK_CONFIG,
    ...scrollbackConfig,
  };

  const state: TerminalBufferState = {
    id: asTerminalBufferId(id),
    lines,
    altLines: [],
    activeBuffer: 'normal',
    cursorState: {
      row: 0,
      col: 0,
      style: 'block',
      visible: true,
      blink: true,
    },
    scrollback: config,
    viewport: {
      startLine: 0,
      endLine: rows - 1,
      scrollTop: 0,
      height: rows,
    },
    dirtyRegions: [],
    modificationSeq: 0,
  };

  return ok(state, 'BUFFER_CREATED', `Buffer created ${cols}x${rows}`);
}

/**
 * Create an empty line of the given width, filled with space cells.
 */
export function createEmptyLine(cols: number): TerminalLine {
  const cells: TerminalCell[] = [];
  for (let i = 0; i < cols; i++) {
    cells.push(EMPTY_CELL);
  }
  return { cells, isWrapped: false, version: 0 };
}

/**
 * Create a cell with the given character, colors, and flags.
 */
export function createBufferCell(
  char: string,
  fg?: TerminalColor,
  bg?: TerminalColor,
  flags?: CellFlags
): TerminalCell {
  return createCell(char, fg, bg, flags);
}

/**
 * Return the default foreground color.
 */
export function defaultForeground(): TerminalColor {
  return defaultTerminalColor();
}

/**
 * Return the default background color.
 */
export function defaultBackground(): TerminalColor {
  return defaultTerminalColor();
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 BUFFER WRITING — writeCharacter, writeString, newline, CR, BS, tab
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Default tab stop positions (every 8 columns).
 */
export const DEFAULT_TAB_STOPS = 8;

/**
 * Write a single character at the cursor position and advance cursor.
 */
export function writeCharacter(
  state: TerminalBufferState,
  char: string,
  fg: TerminalColor,
  bg: TerminalColor,
  flags: CellFlags
): TerminalBufferState {
  const lines = getActiveLines(state);
  const cursor = state.cursorState;
  const row = clamp(cursor.row, 0, lines.length - 1);
  const line = lines[row]!;
  const cols = line.cells.length;

  if (cursor.col >= cols) {
    // Auto-wrap: move to next line
    const afterNewline = newline(state);
    return writeCharacter(afterNewline, char, fg, bg, flags);
  }

  const cell: TerminalCell = { char, width: 1, fg, bg, flags };
  const newCells = [...line.cells];
  newCells[cursor.col] = cell;

  const newLine: TerminalLine = { ...line, cells: newCells, version: line.version + 1 };
  const newLines = [...lines];
  newLines[row] = newLine;

  const newCursor: TerminalCursorState = { ...cursor, col: cursor.col + 1 };
  return applyActiveLines(state, newLines, newCursor);
}

/**
 * Write a string of characters at the cursor, advancing after each.
 */
export function writeString(
  state: TerminalBufferState,
  text: string,
  fg: TerminalColor,
  bg: TerminalColor,
  flags: CellFlags
): TerminalBufferState {
  let current = state;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (ch === '\n') {
      current = newline(current);
    } else if (ch === '\r') {
      current = carriageReturn(current);
    } else if (ch === '\b') {
      current = backspace(current);
    } else if (ch === '\t') {
      current = tab(current);
    } else {
      current = writeCharacter(current, ch, fg, bg, flags);
    }
  }
  return current;
}

/**
 * Move cursor to the next line, scrolling if at the bottom.
 */
export function newline(state: TerminalBufferState): TerminalBufferState {
  const lines = getActiveLines(state);
  const cursor = state.cursorState;
  const maxRow = lines.length - 1;

  if (cursor.row >= maxRow) {
    // Scroll up: remove top line, add empty line at bottom
    const removedLine = lines[0]!;
    const newLines = [...lines.slice(1), createEmptyLine(lines[0]!.cells.length)];
    const scrolledState = applyActiveLines(state, newLines, { ...cursor, col: 0 });
    return addToScrollback(scrolledState, removedLine);
  }

  const newCursor: TerminalCursorState = { ...cursor, row: cursor.row + 1, col: 0 };
  return updateCursor(state, newCursor);
}

/**
 * Move cursor to column 0 on the current line.
 */
export function carriageReturn(state: TerminalBufferState): TerminalBufferState {
  return updateCursor(state, { ...state.cursorState, col: 0 });
}

/**
 * Move cursor one column left (non-destructive).
 */
export function backspace(state: TerminalBufferState): TerminalBufferState {
  const col = Math.max(0, state.cursorState.col - 1);
  return updateCursor(state, { ...state.cursorState, col });
}

/**
 * Advance cursor to the next tab stop.
 */
export function tab(state: TerminalBufferState, tabStops?: number): TerminalBufferState {
  const stops = tabStops ?? DEFAULT_TAB_STOPS;
  const col = state.cursorState.col;
  const nextStop = Math.ceil((col + 1) / stops) * stops;
  const lines = getActiveLines(state);
  const maxCol = lines[state.cursorState.row]?.cells.length ?? 80;
  const clampedCol = Math.min(nextStop, maxCol - 1);
  return updateCursor(state, { ...state.cursorState, col: clampedCol });
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 BUFFER OPERATIONS — clear, delete, insert, erase, scroll
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Clear a specific line from start to end column.
 */
export function clearLine(
  state: TerminalBufferState,
  lineIndex: number,
  start?: number,
  end?: number
): TerminalBufferState {
  const lines = getActiveLines(state);
  if (lineIndex < 0 || lineIndex >= lines.length) return state;

  const line = lines[lineIndex]!;
  const s = start ?? 0;
  const e = end ?? line.cells.length;
  const newCells = [...line.cells];
  for (let i = s; i < Math.min(e, newCells.length); i++) {
    newCells[i] = EMPTY_CELL;
  }

  const newLine: TerminalLine = { ...line, cells: newCells, version: line.version + 1 };
  const newLines = [...lines];
  newLines[lineIndex] = newLine;
  return applyActiveLines(state, newLines, state.cursorState);
}

/**
 * Clear the screen based on mode.
 */
export function clearScreen(
  state: TerminalBufferState,
  mode: 'below' | 'above' | 'all' | 'scrollback'
): TerminalBufferState {
  const lines = getActiveLines(state);
  const cursor = state.cursorState;
  const cols = lines[0]?.cells.length ?? 80;

  switch (mode) {
    case 'below': {
      let cleared = clearLine(state, cursor.row, cursor.col);
      for (let i = cursor.row + 1; i < lines.length; i++) {
        cleared = clearLine(cleared, i);
      }
      return cleared;
    }
    case 'above': {
      let cleared = state;
      for (let i = 0; i < cursor.row; i++) {
        cleared = clearLine(cleared, i);
      }
      cleared = clearLine(cleared, cursor.row, 0, cursor.col + 1);
      return cleared;
    }
    case 'all': {
      const newLines: TerminalLine[] = [];
      for (let i = 0; i < lines.length; i++) {
        newLines.push(createEmptyLine(cols));
      }
      const newCursor: TerminalCursorState = { ...cursor, row: 0, col: 0 };
      return applyActiveLines(state, newLines, newCursor);
    }
    case 'scrollback': {
      // Clear scrollback buffer only, keep visible content
      return {
        ...state,
        modificationSeq: state.modificationSeq + 1,
      };
    }
  }
}

/**
 * Delete a line at the given index, shifting lines up.
 */
export function deleteLine(state: TerminalBufferState, lineIndex: number): TerminalBufferState {
  const lines = getActiveLines(state);
  if (lineIndex < 0 || lineIndex >= lines.length) return state;

  const cols = lines[0]?.cells.length ?? 80;
  const newLines = [...lines];
  newLines.splice(lineIndex, 1);
  newLines.push(createEmptyLine(cols));
  return applyActiveLines(state, newLines, state.cursorState);
}

/**
 * Insert an empty line at the given index, shifting lines down.
 */
export function insertLine(state: TerminalBufferState, atLine: number): TerminalBufferState {
  const lines = getActiveLines(state);
  const cols = lines[0]?.cells.length ?? 80;
  const idx = clamp(atLine, 0, lines.length);

  const newLines = [...lines];
  newLines.splice(idx, 0, createEmptyLine(cols));
  // Remove the last line to keep the total count the same
  newLines.pop();
  return applyActiveLines(state, newLines, state.cursorState);
}

/**
 * Delete count characters at cursor, shifting remaining chars left.
 */
export function deleteCharacters(state: TerminalBufferState, count: number): TerminalBufferState {
  const lines = getActiveLines(state);
  const cursor = state.cursorState;
  const row = clamp(cursor.row, 0, lines.length - 1);
  const line = lines[row]!;
  const newCells = [...line.cells];
  const deleteCount = Math.min(count, newCells.length - cursor.col);

  newCells.splice(cursor.col, deleteCount);
  while (newCells.length < line.cells.length) {
    newCells.push(EMPTY_CELL);
  }

  const newLine: TerminalLine = { ...line, cells: newCells, version: line.version + 1 };
  const newLines = [...lines];
  newLines[row] = newLine;
  return applyActiveLines(state, newLines, cursor);
}

/**
 * Insert count blank characters at cursor, shifting existing chars right.
 */
export function insertCharacters(state: TerminalBufferState, count: number): TerminalBufferState {
  const lines = getActiveLines(state);
  const cursor = state.cursorState;
  const row = clamp(cursor.row, 0, lines.length - 1);
  const line = lines[row]!;
  const totalCols = line.cells.length;

  const newCells = [...line.cells];
  const blanks: TerminalCell[] = [];
  for (let i = 0; i < count; i++) {
    blanks.push(EMPTY_CELL);
  }
  newCells.splice(cursor.col, 0, ...blanks);
  // Trim to original length
  const trimmed = newCells.slice(0, totalCols);

  const newLine: TerminalLine = { ...line, cells: trimmed, version: line.version + 1 };
  const newLines = [...lines];
  newLines[row] = newLine;
  return applyActiveLines(state, newLines, cursor);
}

/**
 * Erase count characters at cursor position (replace with spaces, don't shift).
 */
export function eraseCharacters(state: TerminalBufferState, count: number): TerminalBufferState {
  const lines = getActiveLines(state);
  const cursor = state.cursorState;
  const row = clamp(cursor.row, 0, lines.length - 1);
  const line = lines[row]!;

  const newCells = [...line.cells];
  for (let i = cursor.col; i < Math.min(cursor.col + count, newCells.length); i++) {
    newCells[i] = EMPTY_CELL;
  }

  const newLine: TerminalLine = { ...line, cells: newCells, version: line.version + 1 };
  const newLines = [...lines];
  newLines[row] = newLine;
  return applyActiveLines(state, newLines, cursor);
}

/**
 * Scroll up by the given number of lines. Top lines go to scrollback.
 */
export function scrollUp(state: TerminalBufferState, lineCount: number): TerminalBufferState {
  const lines = getActiveLines(state);
  if (lineCount <= 0 || lines.length === 0) return state;

  const cols = lines[0]!.cells.length;
  const count = Math.min(lineCount, lines.length);
  let current = state;

  // Move top lines to scrollback
  for (let i = 0; i < count; i++) {
    current = addToScrollback(current, lines[i]!);
  }

  const remaining = getActiveLines(current).slice(count);
  const newLines: TerminalLine[] = [...remaining];
  for (let i = 0; i < count; i++) {
    newLines.push(createEmptyLine(cols));
  }

  return applyActiveLines(current, newLines, current.cursorState);
}

/**
 * Scroll down by the given number of lines. Bottom lines are discarded.
 */
export function scrollDown(state: TerminalBufferState, lineCount: number): TerminalBufferState {
  const lines = getActiveLines(state);
  if (lineCount <= 0 || lines.length === 0) return state;

  const cols = lines[0]!.cells.length;
  const count = Math.min(lineCount, lines.length);

  const newLines: TerminalLine[] = [];
  for (let i = 0; i < count; i++) {
    newLines.push(createEmptyLine(cols));
  }
  const remaining = lines.slice(0, lines.length - count);
  newLines.push(...remaining);

  return applyActiveLines(state, newLines, state.cursorState);
}

/**
 * Scroll a region (between top and bottom lines) in a given direction.
 */
export function scrollRegion(
  state: TerminalBufferState,
  top: number,
  bottom: number,
  lineCount: number,
  direction: 'up' | 'down'
): TerminalBufferState {
  const lines = getActiveLines(state);
  if (top < 0 || bottom >= lines.length || top > bottom) return state;

  const cols = lines[0]!.cells.length;
  const region = lines.slice(top, bottom + 1);
  const count = Math.min(lineCount, region.length);
  let newRegion: TerminalLine[];

  if (direction === 'up') {
    const remaining = region.slice(count);
    const blanks: TerminalLine[] = [];
    for (let i = 0; i < count; i++) blanks.push(createEmptyLine(cols));
    newRegion = [...remaining, ...blanks];
  } else {
    const remaining = region.slice(0, region.length - count);
    const blanks: TerminalLine[] = [];
    for (let i = 0; i < count; i++) blanks.push(createEmptyLine(cols));
    newRegion = [...blanks, ...remaining];
  }

  const newLines = [...lines];
  for (let i = top; i <= bottom; i++) {
    newLines[i] = newRegion[i - top]!;
  }

  return applyActiveLines(state, newLines, state.cursorState);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 ALTERNATE BUFFER — switch, save, restore
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Switch to the alternate buffer (used by full-screen apps like vim, less).
 */
export function switchToAlternate(state: TerminalBufferState): TerminalBufferState {
  if (state.activeBuffer === 'alternate') return state;

  const cols = state.lines[0]?.cells.length ?? 80;
  const rows = state.lines.length;
  const altLines: TerminalLine[] = [];
  for (let i = 0; i < rows; i++) {
    altLines.push(createEmptyLine(cols));
  }

  return {
    ...state,
    altLines: state.lines,
    lines: altLines,
    activeBuffer: 'alternate',
    cursorState: { ...state.cursorState, row: 0, col: 0 },
    modificationSeq: state.modificationSeq + 1,
  };
}

/**
 * Switch back to the normal buffer.
 */
export function switchToNormal(state: TerminalBufferState): TerminalBufferState {
  if (state.activeBuffer === 'normal') return state;

  return {
    ...state,
    lines: state.altLines,
    altLines: [],
    activeBuffer: 'normal',
    modificationSeq: state.modificationSeq + 1,
  };
}

/**
 * Check if the alternate buffer is currently active.
 */
export function isAlternateActive(state: TerminalBufferState): boolean {
  return state.activeBuffer === 'alternate';
}

/**
 * Save the current buffer state (cursor + buffer content) for later restore.
 */
export function saveBuffer(state: TerminalBufferState): TerminalBufferState {
  // In the pure state model, saving is recording altLines as a snapshot
  return {
    ...state,
    altLines: state.activeBuffer === 'normal' ? [...state.lines] : state.altLines,
  };
}

/**
 * Restore the saved buffer state.
 */
export function restoreBuffer(state: TerminalBufferState): TerminalBufferState {
  if (state.altLines.length === 0) return state;
  return {
    ...state,
    lines: [...state.altLines],
    modificationSeq: state.modificationSeq + 1,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 SCROLLBACK — addToScrollback, trim, getContent, counts
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add a line to the scrollback buffer (stored above the visible area).
 * Scrollback lines are tracked implicitly via modificationSeq.
 */
export function addToScrollback(state: TerminalBufferState, _line: TerminalLine): TerminalBufferState {
  return {
    ...state,
    modificationSeq: state.modificationSeq + 1,
  };
}

/**
 * Trim scrollback to maxLines. The pure state model doesn't hold scrollback
 * lines in-state — IO manages the actual scrollback store.
 */
export function trimScrollback(state: TerminalBufferState, _maxLines: number): TerminalBufferState {
  return { ...state, modificationSeq: state.modificationSeq + 1 };
}

/**
 * Get scrollback content between start and end line.
 * Returns empty array — scrollback is managed by IO.
 */
export function getScrollbackContent(
  _state: TerminalBufferState,
  _startLine?: number,
  _endLine?: number
): TerminalLine[] {
  return [];
}

/**
 * Return the number of scrollback lines. Pure engine returns 0;
 * actual scrollback is IO-managed.
 */
export function scrollbackLineCount(_state: TerminalBufferState): number {
  return 0;
}

/**
 * Get the visible content (current buffer lines) as strings.
 */
export function getVisibleContent(state: TerminalBufferState): string[] {
  const lines = getActiveLines(state);
  return lines.map(lineToString);
}

/**
 * Get all content (scrollback + visible) as strings.
 */
export function getFullContent(state: TerminalBufferState): string[] {
  // Scrollback is IO-managed, so we only return visible content
  return getVisibleContent(state);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 BUFFER RESIZE — resizeBuffer, reflowLine, reflowBuffer, dirtyRegion
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Resize the buffer to new dimensions, optionally reflowing content.
 */
export function resizeBuffer(
  state: TerminalBufferState,
  newCols: number,
  newRows: number
): TerminalBufferState {
  if (newCols < 1 || newRows < 1) return state;

  const lines = getActiveLines(state);
  const oldCols = lines[0]?.cells.length ?? 80;

  let newLines: TerminalLine[];

  if (shouldReflow(state.scrollback) && newCols !== oldCols) {
    newLines = reflowBuffer(lines, oldCols, newCols);
  } else {
    // Simple resize without reflow
    newLines = lines.map(line => {
      if (line.cells.length === newCols) return line;
      if (line.cells.length < newCols) {
        // Extend with empty cells
        const cells = [...line.cells];
        while (cells.length < newCols) cells.push(EMPTY_CELL);
        return { ...line, cells, version: line.version + 1 };
      }
      // Truncate
      return { ...line, cells: line.cells.slice(0, newCols), version: line.version + 1 };
    });
  }

  // Adjust row count
  while (newLines.length < newRows) {
    newLines.push(createEmptyLine(newCols));
  }
  if (newLines.length > newRows) {
    newLines = newLines.slice(newLines.length - newRows);
  }

  const newCursorRow = clamp(state.cursorState.row, 0, newRows - 1);
  const newCursorCol = clamp(state.cursorState.col, 0, newCols - 1);

  return {
    ...state,
    lines: state.activeBuffer === 'normal' ? newLines : state.lines,
    altLines: state.activeBuffer === 'alternate' ? newLines : state.altLines,
    cursorState: { ...state.cursorState, row: newCursorRow, col: newCursorCol },
    viewport: {
      ...state.viewport,
      endLine: newRows - 1,
      height: newRows,
    },
    modificationSeq: state.modificationSeq + 1,
  };
}

/**
 * Reflow a single line to a new column width.
 */
export function reflowLine(line: TerminalLine, _oldCols: number, newCols: number): TerminalLine[] {
  const chars = line.cells;
  if (chars.length <= newCols) {
    // Pad with empty cells
    const padded = [...chars];
    while (padded.length < newCols) padded.push(EMPTY_CELL);
    return [{ cells: padded, isWrapped: false, version: line.version + 1 }];
  }

  // Split into multiple lines
  const result: TerminalLine[] = [];
  for (let i = 0; i < chars.length; i += newCols) {
    const segment = chars.slice(i, i + newCols);
    while (segment.length < newCols) segment.push(EMPTY_CELL);
    result.push({
      cells: segment,
      isWrapped: i + newCols < chars.length,
      version: line.version + 1,
    });
  }
  return result;
}

/**
 * Reflow all lines in a buffer to a new column width.
 */
export function reflowBuffer(
  lines: TerminalLine[],
  oldCols: number,
  newCols: number
): TerminalLine[] {
  // First, join wrapped lines back together
  const logicalLines: TerminalLine[] = [];
  let currentCells: TerminalCell[] = [];
  let baseVersion = 0;

  for (const line of lines) {
    currentCells.push(...line.cells);
    baseVersion = line.version;
    if (!line.isWrapped) {
      // Strip trailing empty cells
      while (currentCells.length > 0 && currentCells[currentCells.length - 1]!.char === ' ' && currentCells.length > newCols) {
        currentCells.pop();
      }
      logicalLines.push({ cells: currentCells, isWrapped: false, version: baseVersion });
      currentCells = [];
    }
  }
  if (currentCells.length > 0) {
    logicalLines.push({ cells: currentCells, isWrapped: false, version: baseVersion });
  }

  // Now reflow each logical line to the new width
  const result: TerminalLine[] = [];
  for (const logicalLine of logicalLines) {
    const reflowed = reflowLine(logicalLine, oldCols, newCols);
    result.push(...reflowed);
  }

  return result;
}

/**
 * Check whether reflow should be performed based on config.
 */
export function shouldReflow(config: ScrollbackConfig): boolean {
  return config.reflow;
}

/**
 * Compute the dirty region between two buffer states.
 */
export function computeDirtyRegion(
  oldState: TerminalBufferState,
  newState: TerminalBufferState
): Range | null {
  const oldLines = oldState.activeBuffer === 'normal' ? oldState.lines : oldState.altLines;
  const newLines = newState.activeBuffer === 'normal' ? newState.lines : newState.altLines;

  let firstDirty = -1;
  let lastDirty = -1;
  const maxLines = Math.max(oldLines.length, newLines.length);

  for (let i = 0; i < maxLines; i++) {
    const oldLine = oldLines[i];
    const newLine = newLines[i];
    if (!oldLine || !newLine || oldLine.version !== newLine.version) {
      if (firstDirty === -1) firstDirty = i;
      lastDirty = i;
    }
  }

  if (firstDirty === -1) return null;

  return {
    start: { line: firstDirty, column: 0 },
    end: { line: lastDirty, column: (newLines[lastDirty]?.cells.length ?? 80) - 1 },
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function getActiveLines(state: TerminalBufferState): TerminalLine[] {
  return state.activeBuffer === 'normal' ? state.lines : (state.altLines.length > 0 ? state.altLines : state.lines);
}

function applyActiveLines(
  state: TerminalBufferState,
  lines: TerminalLine[],
  cursor: TerminalCursorState
): TerminalBufferState {
  if (state.activeBuffer === 'normal') {
    return {
      ...state,
      lines,
      cursorState: cursor,
      modificationSeq: state.modificationSeq + 1,
    };
  }
  return {
    ...state,
    altLines: lines,
    cursorState: cursor,
    modificationSeq: state.modificationSeq + 1,
  };
}

function updateCursor(state: TerminalBufferState, cursor: TerminalCursorState): TerminalBufferState {
  return { ...state, cursorState: cursor };
}

function lineToString(line: TerminalLine): string {
  let result = '';
  for (const cell of line.cells) {
    result += cell.char;
  }
  // Trim trailing spaces
  return result.replace(/\s+$/, '');
}
